import * as Sentry from "@sentry/browser";

/**
 * Usage analytics via Sentry Application Metrics (+ Logs for error cases).
 *
 * Emission is gated: events are only sent on production deployments and only
 * for customer users (not EMZ staff). The gate is evaluated once in App.tsx
 * via setAnalyticsContext; every track* call is a no-op until it passes.
 *
 * GDPR: events carry only customerId (a legal entity, not a natural person) —
 * no userId, session id, or Sentry user is ever attached, so individual users
 * cannot be separated or identified from the emitted data.
 *
 * Module-level (not a hook) so it can be called from React components as well
 * as plain callbacks (useEntity mutations, fetchEGate).
 */

/**
 * Kill switch from the UAIC-1210 legal/GDPR review. While `false`, every track* call is a
 * no-op regardless of the deployment/customer-user gate.
 */
const LEGAL_APPROVED: boolean = true;

type AttributeValue = string | number | boolean;
export type AnalyticsAttributes = Record<string, AttributeValue | undefined>;

interface AnalyticsContext {
  enabled: boolean;
  customerId?: number;
}

let context: AnalyticsContext = { enabled: false };

/** Drop undefined values so they don't end up as attributes. */
function cleanAttributes(attrs: AnalyticsAttributes): Record<string, AttributeValue> {
  return Object.fromEntries(Object.entries(attrs).filter(([, v]) => v !== undefined)) as Record<
    string,
    AttributeValue
  >;
}

/**
 * Called once the session is known (login + server-info loaded).
 * Safe to call repeatedly; the last call wins.
 */
export function setAnalyticsContext(next: AnalyticsContext) {
  context = { ...next, enabled: next.enabled && LEGAL_APPROVED };
}

/** Called on logout / when the session ends. */
export function clearAnalyticsContext() {
  context = { enabled: false };
}

/**
 * Count a usage event (menu click, entity action, ...).
 * Emits a single Sentry metric with customerId attached.
 */
export function trackEvent(name: string, attrs: AnalyticsAttributes = {}) {
  if (!context.enabled) return;
  Sentry.metrics.count(name, 1, {
    attributes: cleanAttributes({ ...attrs, customerId: context.customerId }),
  });
}

/**
 * Count an error event (api.error, entity.error, app.crash).
 * Emits a metric (failure rate) plus a log (per-event detail). `logAttrs` go on the log
 * only — for per-event values like a concrete record path that would split the metric.
 */
export function trackError(name: string, attrs: AnalyticsAttributes = {}, logAttrs: AnalyticsAttributes = {}) {
  if (!context.enabled) return;
  const attributes = cleanAttributes({ ...attrs, customerId: context.customerId });
  Sentry.metrics.count(name, 1, { attributes });
  Sentry.logger.warn(name, { ...attributes, ...cleanAttributes(logAttrs) });
}

const ID_SEGMENT = /(^|\/)(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi;

/**
 * Replaces numeric and GUID path segments with `{id}`, so the `api.error` metric groups by
 * endpoint like the OpenAPI template the typed client reports, not one series per record.
 */
export function normalizeEndpoint(endpoint: string) {
  return endpoint.replace(ID_SEGMENT, "$1{id}");
}
